import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EllipsisVertical, LogOut, Settings, Shield } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSidebar } from '@/components/ui/sidebar';
import { useLogout, type Me } from '@/features/auth/api';

export function AccountMenu({ me }: { me: Me }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Unlike the nav links, this dropdown isn't dismissed by the sidebar itself — close the mobile
  // drawer explicitly when navigating away, or it stays open over the destination page.
  const { setOpenMobile } = useSidebar();
  const initials = `${me.firstName.at(0) ?? ''}${me.lastName.at(0) ?? ''}`.toUpperCase();
  const logout = useLogout();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label={t('account.menu')}
          className="h-auto w-full justify-start gap-3 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
        >
          <Avatar className="size-8">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col text-left leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate text-sm font-medium">{me.firstName}</span>
            <span className="truncate text-xs text-muted-foreground">
              {me.role === 'ADMIN' ? t('account.administrator') : t('account.user')}
            </span>
          </div>
          <EllipsisVertical className="ml-auto size-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="min-w-56">
        <DropdownMenuItem
          onSelect={() => {
            setOpenMobile(false);
            navigate('/settings');
          }}
        >
          <Settings className="size-4" /> {t('nav.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            setOpenMobile(false);
            navigate('/privacy');
          }}
        >
          <Shield className="size-4" /> {t('account.privacy_policy')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout.mutate()}>
          <LogOut className="size-4" /> {t('account.sign_out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
